import FormBase from '@/components/forms/new/FormBase';
import CallInclusionNameField from './formFields/CallInclusionNameField';
import CallInclusionPriorityField from './formFields/CallInclusionPriorityField';
import CallInclusionAmountField from './formFields/CallInclusionAmountField';
import CallInclusionTypeField from './formFields/CallInclusionTypeField';
import CallInclusionAppliesToField from './formFields/CallInclusionAppliesToField';
import { Field } from 'formik';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { Button } from 'components/v4';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import BreadcrumbTrail from 'components/BreadcrumbTrail';

// An InternationalCallRate that shares a prefix with a national CallRate is
// hidden from the options — otherwise it's easy to accidentally pick the
// pricier international rate when a national rate already covers that
// prefix (#2421). Remaining InternationalCallRate options keep their `type`
// so CallInclusionAppliesToField can show a globe icon next to them.
export const buildAvailableRateOptions = (availableRates = []) => {
    const callRatePrefixes = new Set(
        availableRates
            .filter((r) => r.type === 'CallRate' && r.prefix)
            .map((r) => r.prefix),
    );

    return availableRates
        .filter(
            (r) =>
                r.type !== 'InternationalCallRate' ||
                !callRatePrefixes.has(r.prefix),
        )
        .map((r) => ({
            label: r.prefix ? `${r.name} (${r.prefix})` : r.name,
            value: r.id,
            type: r.type,
        }));
};

const CallInclusionsForm = ({
    mode,
    initialValues,
    isLoading,
    isDeleting,
    onBack,
    onSubmit,
    onDelete,
    darkMode,
    availableRates,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const breadCrumbTrail = [
        { label: 'Calling Inclusions', path: ['', { index: 0 }] },
        { label: 'Add Calling Inclusions ', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={onBack}
            />
            <FormBase
                //header={mode === 'edit' ? 'Edit Call Inclusion' : 'Add Call Inclusion'}
                subTitleHeader={false}
                initialValues={initialValues}
                isLoading={isLoading}
                finalFormSubmit={onSubmit}
                onBack={onBack}
                customClick={true}
                extraButton={
                    mode === 'edit' && (
                        <Flex justify="flex-end" width="100%">
                            <Button
                                w={142}
                                colorScheme="red"
                                onClick={onOpen}
                                isDisabled={isDeleting || isLoading}>
                                Delete
                            </Button>
                        </Flex>
                    )
                }>
                {({ errors, touched, setFieldValue }) => {
                    const fields = [
                        <CallInclusionNameField
                            key="name"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <CallInclusionPriorityField
                            key="priority"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <CallInclusionAmountField
                            key="inclusionAmount"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <Field
                            key="type"
                            name="visibleScope"
                            component={CallInclusionTypeField}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                        />,
                        <CallInclusionAppliesToField
                            key="appliesTo"
                            darkMode={darkMode}
                            error={errors}
                            onChange={(val) => setFieldValue('appliesTo', val)}
                            options={buildAvailableRateOptions(availableRates)}
                            disableStackHeight={true}
                        />,
                    ];

                    return fields;
                }}
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isOpen}
                onClose={onClose}
                itemName={initialValues?.callInclsions || 'Item Code Mapping'}
                onConfirm={() => {
                    onDelete();
                    onClose();
                }}
                isDeleting={isDeleting}
                itemType="Item Code Mapping"
            />
        </>
    );
};

export default CallInclusionsForm;
